import { useState, useEffect, useRef, useMemo } from 'react';
import { getPracticeQuestions } from '../utils/api';

/* ─────────── content parsing (drives every game from the real lesson text) ─────────── */

function parseLesson(content) {
  const text = content || '';
  const lines = text.split(/\n/).map(l => l.trim());
  const isHeading = l => /^[A-Z][A-Z0-9 &/()'-]{3,}$/.test(l) && !/^OBJECTION/.test(l);

  // OBJECTION: "..."  /  Rep: "..."  pairs
  const objections = [];
  for (let i = 0; i < lines.length;) {
    const mo = lines[i].match(/^OBJECTION:\s*["“”]?(.+?)["“”]?\s*$/);
    if (mo) {
      const obj = mo[1].replace(/^["“”]|["“”]$/g, '').trim();
      let j = i + 1; while (j < lines.length && !/^Rep:/i.test(lines[j])) j++;
      let k = j + 1; const buf = [];
      while (k < lines.length) {
        const l = lines[k];
        if (/^OBJECTION:/.test(l) || /^If They|^If You Get|^Mental Framework/i.test(l) || isHeading(l)) break;
        if (l) buf.push(l);
        k++;
      }
      const resp = buf.join(' ').replace(/\([^)]*\)/g, ' ').replace(/["“”]/g, '').replace(/\s+/g, ' ').trim();
      if (obj && resp) objections.push({ obj, resp });
      i = k; continue;
    }
    i++;
  }

  // quoted rep script lines (skip the objection prompts themselves)
  const objSet = new Set(objections.map(o => o.obj.toLowerCase()));
  const scripts = [...text.matchAll(/["“]([^"“”]{18,150})["”]/g)]
    .map(x => x[1].replace(/\s+/g, ' ').trim())
    .filter(s => s.split(' ').length >= 4 && !objSet.has(s.toLowerCase()));
  const uniqScripts = [...new Set(scripts)];

  return { objections, scripts: uniqScripts };
}

/* ─────────── tiny WebAudio sound kit (no asset files) ─────────── */
function useSound(enabledRef) {
  const ctxRef = useRef(null);
  const ac = () => { if (!ctxRef.current) { const C = window.AudioContext || window.webkitAudioContext; if (C) ctxRef.current = new C(); } return ctxRef.current; };
  const beep = (freq, t0, dur, type = 'sine', gain = 0.08) => {
    const ctx = ac(); if (!ctx || !enabledRef.current) return;
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = type; o.frequency.value = freq;
    o.connect(g); g.connect(ctx.destination);
    const now = ctx.currentTime + t0;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(gain, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    o.start(now); o.stop(now + dur);
  };
  return {
    right: () => { beep(660, 0, 0.12, 'triangle'); beep(880, 0.08, 0.16, 'triangle'); },
    wrong: () => { beep(180, 0, 0.22, 'sawtooth', 0.05); },
    levelup: () => { [523, 659, 784, 1046].forEach((f, i) => beep(f, i * 0.09, 0.18, 'triangle', 0.07)); },
    tick: () => { beep(440, 0, 0.04, 'square', 0.02); },
  };
}

/* ─────────── XP / belt system ─────────── */
const BELTS = ['White', 'Yellow', 'Orange', 'Green', 'Blue', 'Purple', 'Brown', 'Red', 'Black'];
const BELT_COLOR = { White: '#eee', Yellow: '#ffd54f', Orange: '#ff9800', Green: '#6586bc', Blue: '#42a5f5', Purple: '#ab47bc', Brown: '#8d6e63', Red: '#ef5350', Black: '#333' };
const levelFor = xp => Math.floor(xp / 100) + 1;
const beltFor = lvl => BELTS[Math.min(lvl - 1, BELTS.length - 1)];

function loadXP() { try { return parseInt(localStorage.getItem('cpr_arena_xp') || '0') || 0; } catch { return 0; } }
function saveXP(v) { try { localStorage.setItem('cpr_arena_xp', String(v)); } catch {} }

/* shuffle helper */
const shuffle = a => { const x = [...a]; for (let i = x.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [x[i], x[j]] = [x[j], x[i]]; } return x; };
const firstSentence = s => { const m = s.match(/^.*?[.!?](\s|$)/); return (m ? m[0] : s).trim(); };

/* ═══════════════════════ MAIN ARENA ═══════════════════════ */
export default function PracticeArena({ content, lessonId }) {
  const data = useMemo(() => parseLesson(content), [content]);
  const [quizQs, setQuizQs] = useState([]);
  const [game, setGame] = useState(null);          // null | 'objection' | 'quiz' | 'rapid'
  const [xp, setXp] = useState(loadXP());
  const [combo, setCombo] = useState(0);
  const [muted, setMuted] = useState(false);
  const [xpPops, setXpPops] = useState([]);
  const [beltUp, setBeltUp] = useState(false);
  const enabledRef = useRef(true);
  useEffect(() => { enabledRef.current = !muted; }, [muted]);
  const sound = useSound(enabledRef);

  useEffect(() => {
    let on = true;
    if (lessonId) getPracticeQuestions(lessonId).then(d => { if (on) setQuizQs(d.questions || []); }).catch(() => {});
    return () => { on = false; };
  }, [lessonId]);

  const level = levelFor(xp);
  const belt = beltFor(level);
  const xpInLevel = xp % 100;

  function award(correct) {
    if (correct) {
      const newCombo = combo + 1;
      const gain = 10 * Math.min(newCombo, 5);
      const before = level;
      const nx = xp + gain;
      setXp(nx); saveXP(nx); setCombo(newCombo);
      sound.right();
      const id = Math.random();
      setXpPops(p => [...p, { id, val: '+' + gain }]);
      setTimeout(() => setXpPops(p => p.filter(x => x.id !== id)), 1100);
      if (levelFor(nx) > before) { sound.levelup(); setBeltUp(true); setTimeout(() => setBeltUp(false), 2500); }
    } else {
      setCombo(0); sound.wrong();
    }
  }

  const has = { objection: data.objections.length >= 3, quiz: quizQs.length >= 1, rapid: data.objections.length >= 3 };
  const anyGame = has.objection || has.quiz || has.rapid;

  return (
    <div className="practice-arena">
      <div className="arena-card">
        {/* header: belt + level + xp */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div className={beltUp ? 'belt-up' : ''} style={{ width: 44, height: 44, borderRadius: 12, background: BELT_COLOR[belt], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '2px solid rgba(255,255,255,0.5)' }}>
            <span style={{ fontSize: 22 }}>🥋</span>
          </div>
          <div style={{ flex: 1, position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontWeight: 800, fontSize: 14 }}>{belt} Belt · Lv {level}</span>
              <span style={{ fontSize: 11, opacity: 0.8 }}>{xp} XP</span>
            </div>
            <div className="arena-xpbar" style={{ marginTop: 4 }}><div className="arena-xpfill" style={{ width: xpInLevel + '%' }} /></div>
            {xpPops.map(p => <span key={p.id} className="xp-pop" style={{ right: 0, top: -6 }}>{p.val}</span>)}
          </div>
          <button onClick={() => setMuted(m => !m)} title={muted ? 'Unmute' : 'Mute'} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', opacity: 0.85 }}>{muted ? '🔇' : '🔊'}</button>
        </div>

        {/* combo meter */}
        {combo > 1 && (
          <div style={{ textAlign: 'center', marginBottom: 8, fontWeight: 800, fontSize: 13, color: '#ffd54f' }}>
            <span key={combo} className="combo-flash">🔥 {combo}× combo!</span>
          </div>
        )}

        {/* menu vs active game */}
        {!game ? (
          <>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 2 }}>🎮 Practice Arena</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 12 }}>Drill this lesson. Earn XP. Climb the belts.</div>
            {!anyGame && <div style={{ fontSize: 12.5, opacity: 0.8, background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: 10 }}>No drills for this lesson yet — they appear automatically on lessons with scripts & objections.</div>}
            {has.objection && <GameBtn icon="🎯" title="Beat the Objection" sub={`${data.objections.length} real objections`} onClick={() => setGame('objection')} />}
            {has.quiz && <GameBtn icon="🧠" title="Sales Knowledge" sub={`${quizQs.length} questions · know your stuff`} onClick={() => setGame('quiz')} />}
            {has.rapid && <GameBtn icon="⚡" title="Rapid Match" sub="Right response? Fast!" onClick={() => setGame('rapid')} />}
          </>
        ) : (
          <>
            <button onClick={() => setGame(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginBottom: 10 }}>← Arena menu</button>
            {game === 'objection' && <BeatObjection data={data} award={award} />}
            {game === 'quiz' && <SalesQuiz questions={quizQs} award={award} />}
            {game === 'rapid' && <RapidMatch data={data} award={award} sound={sound} />}
          </>
        )}
      </div>
    </div>
  );
}

function GameBtn({ icon, title, sub, onClick }) {
  return (
    <button className="arena-game-btn" onClick={onClick} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 12, padding: '12px 14px', color: '#fff', cursor: 'pointer', marginBottom: 9 }}>
      <span style={{ fontSize: 26 }}>{icon}</span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontWeight: 800, fontSize: 14 }}>{title}</span>
        <span style={{ display: 'block', fontSize: 11.5, opacity: 0.8 }}>{sub}</span>
      </span>
      <span style={{ fontSize: 18, opacity: 0.7 }}>▶</span>
    </button>
  );
}

/* ─────────── GAME 1: Beat the Objection ─────────── */
function BeatObjection({ data, award }) {
  const [round, setRound] = useState(0);
  const [picked, setPicked] = useState(null);
  const q = useMemo(() => {
    const correct = data.objections[round % data.objections.length];
    const others = shuffle(data.objections.filter(o => o.resp !== correct.resp)).slice(0, 2);
    const opts = shuffle([correct, ...others].map(o => ({ text: firstSentence(o.resp), ok: o.resp === correct.resp })));
    return { obj: correct.obj, opts };
  }, [round, data]);

  function pick(i) {
    if (picked !== null) return;
    setPicked(i);
    award(q.opts[i].ok);
    setTimeout(() => { setPicked(null); setRound(r => r + 1); }, 1400);
  }

  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>🎯 They say at the door:</div>
      <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 10, padding: '10px 12px', fontStyle: 'italic', marginBottom: 10, fontSize: 14 }}>“{q.obj}”</div>
      <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>Pick the best rep response:</div>
      {q.opts.map((o, i) => {
        const show = picked !== null;
        const cls = show && o.ok ? 'answer-right' : show && picked === i && !o.ok ? 'answer-wrong' : '';
        const bg = show ? (o.ok ? '#1e427b' : picked === i ? '#a23b34' : 'rgba(255,255,255,0.08)') : 'rgba(255,255,255,0.1)';
        return (
          <button key={i} className={'arena-opt ' + cls} onClick={() => pick(i)} disabled={show}
            style={{ width: '100%', textAlign: 'left', background: bg, border: '1px solid rgba(255,255,255,0.18)', borderRadius: 10, padding: '10px 12px', color: '#fff', cursor: show ? 'default' : 'pointer', marginBottom: 8, fontSize: 13, lineHeight: 1.4 }}>
            {show && o.ok ? '✅ ' : show && picked === i ? '❌ ' : ''}{o.text}
          </button>
        );
      })}
    </div>
  );
}

/* ─────────── GAME 2: Sales Knowledge (authored questions) ─────────── */
function SalesQuiz({ questions, award }) {
  const [order] = useState(() => shuffle(questions.map((_, i) => i)));
  const [round, setRound] = useState(0);
  const [picked, setPicked] = useState(null);

  const q = questions[order[round % order.length]];
  const opts = useMemo(() => shuffle(q.options.map(o => ({ text: o, ok: String(o).toLowerCase() === String(q.answer).toLowerCase() }))), [round, q]);

  function pick(i) {
    if (picked !== null) return;
    setPicked(i);
    award(opts[i].ok);
    setTimeout(() => { setPicked(null); setRound(r => r + 1); }, 1400);
  }

  const show = picked !== null;
  const cap = s => String(s).charAt(0).toUpperCase() + String(s).slice(1);
  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>🧠 {q.type === 'tf' ? 'True or False?' : 'Sales knowledge'}</div>
      <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 10, padding: '11px 13px', marginBottom: 10, fontSize: 14, lineHeight: 1.45 }}>{q.prompt}</div>
      {opts.map((o, i) => {
        const cls = show && o.ok ? 'answer-right' : show && picked === i && !o.ok ? 'answer-wrong' : '';
        const bg = show ? (o.ok ? '#1e427b' : picked === i ? '#a23b34' : 'rgba(255,255,255,0.08)') : 'rgba(255,255,255,0.1)';
        return (
          <button key={i} className={'arena-opt ' + cls} onClick={() => pick(i)} disabled={show}
            style={{ width: '100%', textAlign: 'left', background: bg, border: '1px solid rgba(255,255,255,0.18)', borderRadius: 10, padding: '10px 12px', color: '#fff', cursor: show ? 'default' : 'pointer', marginBottom: 8, fontSize: 13, lineHeight: 1.4 }}>
            {show && o.ok ? '✅ ' : show && picked === i ? '❌ ' : ''}{cap(o.text)}
          </button>
        );
      })}
    </div>
  );
}

/* ─────────── GAME 3: Rapid Match (true/false) ─────────── */
function RapidMatch({ data, award }) {
  const [round, setRound] = useState(0);
  const [picked, setPicked] = useState(null);

  const q = useMemo(() => {
    const correct = data.objections[round % data.objections.length];
    const real = Math.random() > 0.5;
    let resp = correct.resp;
    if (!real) {
      const other = shuffle(data.objections.filter(o => o.resp !== correct.resp))[0];
      resp = other ? other.resp : correct.resp;
    }
    return { obj: correct.obj, resp: firstSentence(resp), real };
  }, [round, data]);

  function answer(saysReal) {
    if (picked !== null) return;
    const ok = saysReal === q.real;
    setPicked(saysReal);
    award(ok);
    setTimeout(() => { setPicked(null); setRound(r => r + 1); }, 1200);
  }

  const show = picked !== null;
  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>🧠 Is this the right response?</div>
      <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 10, padding: '10px 12px', marginBottom: 8, fontSize: 13 }}>
        <div style={{ opacity: 0.75, fontSize: 11, marginBottom: 3 }}>HOMEOWNER:</div>
        <div style={{ fontStyle: 'italic' }}>“{q.obj}”</div>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 13 }}>
        <div style={{ opacity: 0.75, fontSize: 11, marginBottom: 3 }}>REP:</div>
        <div style={{ fontStyle: 'italic' }}>“{q.resp}”</div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {[{ l: '✅ Correct', v: true }, { l: '❌ Wrong', v: false }].map(b => {
          const bg = show ? (b.v === q.real ? '#1e427b' : picked === b.v ? '#a23b34' : 'rgba(255,255,255,0.08)') : 'rgba(255,255,255,0.12)';
          const cls = show && b.v === q.real ? 'answer-right' : show && picked === b.v && b.v !== q.real ? 'answer-wrong' : '';
          return (
            <button key={String(b.v)} className={'arena-opt ' + cls} onClick={() => answer(b.v)} disabled={show}
              style={{ flex: 1, background: bg, border: '1px solid rgba(255,255,255,0.18)', borderRadius: 10, padding: '12px', color: '#fff', cursor: show ? 'default' : 'pointer', fontWeight: 800, fontSize: 14 }}>
              {b.l}
            </button>
          );
        })}
      </div>
      {show && <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, opacity: 0.85 }}>{q.real ? 'That was the real script.' : 'That response belongs to a different objection.'}</div>}
    </div>
  );
}
